/**
 * Formatação única da Rota (SPEC 5.2, 9.4).
 *
 * - Objetos `Intl` instanciados uma vez neste módulo.
 * - Dinheiro chega em `Cents` e só aqui vira reais (÷ 100). Nada de `toFixed` para exibir.
 * - Taxas chegam como fração 0..1 (`Rate`).
 * - Sinal negativo sempre U+2212 ("−"). `null` vira "—" (o componente põe `aria-label="sem dado"`).
 */
import type { CompareMode, DateKey, KpiFormat, Ms } from '@rota/contracts';
import { relativeTime, TZ, toHHMM } from './time';

export const MINUS = '−';
export const EMPTY = '—';
export const THIN_SPACE = ' ';
const NBSP = ' ';
const NNBSP = ' ';

const LOCALE = 'pt-BR';

const nf = {
  brl0: new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }),
  brl2: new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  brlCompact: new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }),
  int: new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 }),
  intCompact: new Intl.NumberFormat(LOCALE, { notation: 'compact', maximumFractionDigits: 1 }),
  pct1: new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }),
  pct2: new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  dec1: new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  dec2: new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
} as const;

const timeFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const dateShortFmt = new Intl.DateTimeFormat(LOCALE, { timeZone: 'UTC', day: 'numeric', month: 'short' });
const dateLongFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const dateFullFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const isMoney = (f: KpiFormat): f is 'brl0' | 'brl2' | 'brlCompact' =>
  f === 'brl0' || f === 'brl2' || f === 'brlCompact';

const isBad = (v: number | null | undefined): v is null | undefined =>
  v === null || v === undefined || !Number.isFinite(v);

/** Troca o hífen-menos pelo sinal tipográfico U+2212 e normaliza espaços estreitos. */
function typographic(s: string): string {
  return s.replace(/-/g, MINUS).replace(new RegExp(NNBSP, 'g'), NBSP);
}

function withSign(text: string, value: number, sign: boolean): string {
  if (!sign || value === 0) return text;
  return value > 0 ? `+${text}` : text;
}

export interface FmtOptions {
  /** Mostra "+" em valores positivos (deltas, diferenças). `pp` já mostra por padrão. */
  sign?: boolean;
  /** Acima deste valor absoluto (em reais ou unidades), usa notação compacta ("R$ 14,4 mil"). */
  compactAbove?: number;
}

/** Formata um valor conforme o `KpiFormat` (5.2). */
export function fmt(value: number | null | undefined, format: KpiFormat, opts: FmtOptions = {}): string {
  if (isBad(value)) return EMPTY;
  const sign = opts.sign ?? format === 'pp';
  const abs = Math.abs(value);
  let text: string;
  switch (format) {
    case 'brl0':
    case 'brl2':
    case 'brlCompact': {
      const reais = value / 100;
      const useCompact =
        format === 'brlCompact' || (opts.compactAbove !== undefined && Math.abs(reais) >= opts.compactAbove);
      text = (useCompact ? nf.brlCompact : format === 'brl2' ? nf.brl2 : nf.brl0).format(reais);
      break;
    }
    case 'int':
      text =
        opts.compactAbove !== undefined && abs >= opts.compactAbove
          ? nf.intCompact.format(value)
          : nf.int.format(value);
      break;
    case 'pct1':
      text = nf.pct1.format(value);
      break;
    case 'pct2':
      text = nf.pct2.format(value);
      break;
    case 'pp':
      text = `${nf.dec1.format(value * 100)}${NBSP}pp`;
      break;
    case 'ratio2':
      text = nf.dec2.format(value);
      break;
    case 'mult1':
      text = `${nf.dec1.format(value)}×`;
      break;
    case 'mmss':
      text = (value < 0 ? '-' : '') + fmtMmss(abs);
      break;
    case 'min':
      text = (value < 0 ? '-' : '') + fmtMinutes(abs);
      break;
    case 'days': {
      const d = Math.round(value);
      text = `${nf.int.format(d)}${NBSP}${Math.abs(d) === 1 ? 'dia' : 'dias'}`;
      break;
    }
    case 'sec':
      text = `${nf.int.format(Math.round(value))}${NBSP}s`;
      break;
    default: {
      const never: never = format;
      return String(never);
    }
  }
  // Arredondou para zero ("−0,0 pp", "+R$ 0"): zero não tem sinal.
  const out = typographic(text);
  if (!/[1-9]/.test(out)) return out.replace(MINUS, '');
  return withSign(out, value, sign);
}

export interface FmtParts {
  prefix: string;
  number: string;
  suffix: string;
}

/** Partes para compor o "R$" menor (9.3): `{ prefix: 'R$', number: '14.371', suffix: '' }`. */
export function fmtParts(value: number, format: KpiFormat): FmtParts {
  if (!Number.isFinite(value)) return { prefix: '', number: EMPTY, suffix: '' };
  if (isMoney(format)) {
    const reais = value / 100;
    const f = format === 'brlCompact' ? nf.brlCompact : format === 'brl2' ? nf.brl2 : nf.brl0;
    let prefix = '';
    let number = '';
    let suffix = '';
    let seenNumber = false;
    for (const p of f.formatToParts(reais)) {
      if (p.type === 'currency') prefix = p.value;
      else if (p.type === 'literal' && /^[\s  ]+$/.test(p.value)) {
        if (seenNumber) suffix += NBSP;
      } else if (p.type === 'compact') suffix += p.value;
      else if (p.type === 'minusSign') number += MINUS;
      else {
        seenNumber = true;
        number += p.value;
      }
    }
    return { prefix, number: typographic(number), suffix: suffix.trim() ? `${NBSP}${suffix.trim()}` : '' };
  }
  const full = fmt(value, format);
  const m = /^([+−]?[\d.,:]+)(.*)$/.exec(full);
  if (!m) return { prefix: '', number: full, suffix: '' };
  return { prefix: '', number: m[1] ?? full, suffix: m[2] ?? '' };
}

/**
 * Delta para `DeltaBadge`: "+18,4%", "−2,1 pp", "+R$ 1.240".
 * - `relative`: fração (0,184 → "+18,4%");
 * - `pp`: diferença entre taxas em fração (0,021 → "+2,1 pp");
 * - `abs`: diferença absoluta no `format` dado (padrão `int`).
 */
export function fmtDelta(
  delta: number | null | undefined,
  kind: 'relative' | 'pp' | 'abs',
  format: KpiFormat = 'int',
): string {
  if (isBad(delta)) return EMPTY;
  if (kind === 'relative') return withSign(typographic(nf.pct1.format(delta)), delta, true);
  if (kind === 'pp') return fmt(delta, 'pp', { sign: true });
  return fmt(delta, format, { sign: true });
}

/** "14:32" em São Paulo. */
export function fmtTime(ms: Ms): string {
  if (!Number.isFinite(ms)) return EMPTY;
  return timeFmt.format(ms);
}

function keyToUtcNoon(key: DateKey): number {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12);
}

/** "21 out" (short) · "quarta, 21 de outubro" (long) · "21 de outubro de 2026" (full). */
export function fmtDate(dateKey: DateKey, style: 'short' | 'long' | 'full'): string {
  const t = keyToUtcNoon(dateKey);
  if (style === 'short') return dateShortFmt.format(t).replace(/\./g, '').replace(/ de /g, ' ');
  if (style === 'full') return dateFullFmt.format(t);
  return dateLongFmt.format(t).replace('-feira', '');
}

/** "agora", "há 3 min", "há 2 h". */
export function fmtRelative(ms: Ms, now: Ms): string {
  return relativeTime(ms, now);
}

/** Segundos → "17:35" (ou "1:02:03"). */
export function fmtMmss(sec: number): string {
  if (!Number.isFinite(sec)) return EMPTY;
  const total = Math.max(0, Math.round(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s < 10 ? `0${s}` : String(s);
  if (h > 0) return `${h}:${m < 10 ? `0${m}` : m}:${ss}`;
  return `${m < 10 ? `0${m}` : m}:${ss}`;
}

/** Minutos → "12 min" ou "1 h 12 min". */
export function fmtMinutes(minutes: number): string {
  if (!Number.isFinite(minutes)) return EMPTY;
  const total = Math.round(minutes);
  if (total < 60) return `${total}${NBSP}min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h}${NBSP}h` : `${h}${NBSP}h ${m}${NBSP}min`;
}

/** Duração em ms → "há"-agnóstico: "45 s", "12 min", "2 h 5 min". */
export function fmtDuration(ms: number): string {
  if (!Number.isFinite(ms)) return EMPTY;
  const sec = Math.round(Math.abs(ms) / 1000);
  if (sec < 60) return `${sec}${NBSP}s`;
  return fmtMinutes(sec / 60);
}

const SPOKEN_PCT = (s: string): string => s.replace('%', ' por cento');

/**
 * Valor para leitor de tela: "R$ 14.371", "21,9 por cento", "17 minutos e 35 segundos",
 * "mais 2,4 pontos percentuais". `null` → "sem dado".
 */
export function ariaValue(value: number | null | undefined, format: KpiFormat): string {
  if (isBad(value)) return 'sem dado';
  const spokenSign = (s: string): string =>
    s.replace(/^\+/, 'mais ').replace(new RegExp(`^${MINUS}`), 'menos ');
  switch (format) {
    case 'pct1':
    case 'pct2':
      return spokenSign(SPOKEN_PCT(fmt(value, format)));
    case 'pp':
      return spokenSign(fmt(value, 'pp').replace(/\s?pp$/, ' pontos percentuais'));
    case 'mmss': {
      const total = Math.round(Math.abs(value));
      const m = Math.floor(total / 60);
      const s = total % 60;
      const mm = `${m} ${m === 1 ? 'minuto' : 'minutos'}`;
      const ss = `${s} ${s === 1 ? 'segundo' : 'segundos'}`;
      if (m === 0) return ss;
      return s === 0 ? mm : `${mm} e ${ss}`;
    }
    case 'min':
      return fmtMinutes(value)
        .replace(/\bmin\b/, 'minutos')
        .replace(/\bh\b/, 'horas');
    case 'sec':
      return `${Math.round(value)} segundos`;
    case 'mult1':
      return `${fmt(value, 'mult1').replace('×', '')} vezes`;
    default:
      return spokenSign(fmt(value, format));
  }
}

/** Rótulo da base de comparação para o delta: "vs ontem às 14:32" (3.3). */
export function fmtCompareLabel(mode: CompareMode, at: Ms): string {
  switch (mode) {
    case 'yesterday_same_time':
      return `vs ontem às ${toHHMM(at)}`;
    case 'same_weekday':
      return 'vs mesmo dia da semana passada';
    case 'avg_4w_same_weekday':
      return 'vs média de 4 semanas';
    case 'previous_period':
      return 'vs período anterior';
    case 'previous_month':
      return 'vs mesmo período do mês passado';
    case 'none':
      return '';
    default: {
      const never: never = mode;
      return String(never);
    }
  }
}

/** Contagem com rótulo no singular/plural: (3, 'venda', 'vendas') → "3 vendas". */
export function fmtCount(n: number, singular: string, plural: string): string {
  return `${nf.int.format(n)}${NBSP}${Math.abs(n) === 1 ? singular : plural}`;
}
